/**
 * VERSION C — "TYPE IS THE FRAME"
 *
 * The question this version asks: **what if the type IS the picture, not a caption on one?**
 *
 * There are no illustrations and no scenes. Every beat is a typographic composition that fills
 * the canvas edge to edge, and the photography appears INSIDE the letterforms
 * (`background-clip: text`) or as a textured colour field behind them. The word you are hearing
 * is the largest object on screen at all times.
 *
 * ⛔ DEVANAGARI LAW (research 01 §I, 03 §9): no letter-spacing and NO PER-GLYPH ANIMATION —
 * splitting a Devanagari word shears the shirorekha and orphans matras into dotted circles.
 * So every move here is on the WHOLE WORD or on a MASK travelling over it. That constraint is
 * why this version leans on wipes, knockouts and scale rather than flying letters.
 * ⛔ `background-clip:text` and `mix-blend-mode` flatten preserve-3d, so the perspective tunnel
 * in P5/P8 uses plain-coloured type; knockout type never sits inside a 3D subtree.
 *
 * Register: poster. Bold colour fields that still read as MATERIAL (grain + a photographic wash),
 * never as flat planes — that was the v15 rejection.
 * Type: Modak (hero) / Biryani 900 (secondary) / Khand 600 (labels).
 */
import React from "react";
import { AbsoluteFill, Audio, Img, staticFile, Easing } from "remotion";
import { loadFont as Modak } from "@remotion/google-fonts/Modak";
import { loadFont as Biryani } from "@remotion/google-fonts/Biryani";
import { loadFont as Khand } from "@remotion/google-fonts/Khand";
import { Plate, Label, Grain, useKeys, useSpringAt, useStep, devaBlock } from "./kit45";

const hero = Modak("normal", { weights: ["400"], subsets: ["devanagari"] }).fontFamily;
const sec = Biryani("normal", { weights: ["900", "700"], subsets: ["devanagari", "latin"] }).fontFamily;
const lab = Khand("normal", { weights: ["600"], subsets: ["devanagari", "latin"] }).fontFamily;

const sf = (p: string) => staticFile(p);
const IMG = {
  embers: sf("sanatan45/C/C1_embers.png"),
  copper: sf("sanatan45/C/C2_copper_macro.png"),
  crowd: sf("sanatan45/C/C3_gathering.png"),
  roots: sf("sanatan45/C/C4_roots_macro.png"),
  paper: sf("sanatan45/C/C5_paper_macro.png"),
  stone: sf("sanatan45/C/C6_stone_macro.png"),
};

/**
 * FIELD — a colour ground that is never flat: a duotone photographic wash sits under the colour
 * at low opacity, plus grain. This is the answer to "colour is allowed, FLAT colour is not".
 */
const Field: React.FC<{ color: string; img: string; wash?: number; blend?: React.CSSProperties["mixBlendMode"] }> = ({
  color,
  img,
  wash = 0.34,
  blend = "overlay",
}) => (
  <AbsoluteFill style={{ backgroundColor: color, overflow: "hidden" }}>
    <Img
      src={img}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        opacity: wash,
        mixBlendMode: blend,
        filter: "saturate(0.35) contrast(1.15)",
      }}
    />
  </AbsoluteFill>
);

/**
 * KNOCKOUT — photography showing through the letterforms. The single strongest move this
 * version owns. ⛔ never nest inside a preserve-3d subtree.
 */
const Knockout: React.FC<{
  at: number;
  text: string;
  img: string;
  size: number;
  font?: string;
  weight?: number;
  scale?: number;
  style?: React.CSSProperties;
}> = ({ at, text, img, size, font = hero, weight = 400, scale = 1.35, style }) => {
  const w = useKeys([[at, 0], [at + 0.3, 100]], Easing.out(Easing.cubic));
  const s = useSpringAt(at, { damping: 13, stiffness: 200, mass: 0.7 });
  return (
    <span
      data-audit="knockout"
      style={{
        ...devaBlock,
        display: "inline-block",
        fontFamily: font,
        fontWeight: weight,
        fontSize: size,
        backgroundImage: `url(${img})`,
        backgroundSize: `${scale * 100}% auto`,
        backgroundPosition: "center 38%",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        color: "transparent",
        WebkitTextFillColor: "transparent",
        clipPath: `inset(-25% ${100 - w}% -20% -3%)`,
        transform: `scale(${(0.9 + s * 0.1).toFixed(3)})`,
        whiteSpace: "pre",
        ...style,
      }}
    >
      {text}
    </span>
  );
};

/** Solid type with a mask wipe. Whole-word only. */
const Word: React.FC<{
  at: number;
  text: string;
  size: number;
  color: string;
  font?: string;
  weight?: number;
  from?: "left" | "up";
  style?: React.CSSProperties;
}> = ({ at, text, size, color, font = sec, weight = 900, from = "left", style }) => {
  const w = useKeys([[at, 0], [at + 0.26, 100]], Easing.out(Easing.cubic));
  const s = useSpringAt(at, { damping: 14, stiffness: 190, mass: 0.75 });
  return (
    <span
      data-audit="word"
      style={{
        ...devaBlock,
        display: "inline-block",
        fontFamily: font,
        fontWeight: weight,
        fontSize: size,
        color,
        clipPath:
          from === "left"
            ? `inset(-25% ${100 - w}% -20% -3%)`
            : `inset(${115 - w * 1.15}% -4% -20% -3%)`,
        transform: `scale(${(0.93 + s * 0.07).toFixed(3)})`,
        whiteSpace: "pre",
        ...style,
      }}
    >
      {text}
    </span>
  );
};

const OCC: [number, string][] = [
  [0.30, "जन्म"],
  [1.58, "मुंडन"],
  [2.40, "शादी"],
  [3.28, "गृह प्रवेश"],
  [4.26, "हवन"],
  [5.94, "अंतिम संस्कार"],
];

/**
 * ⛔ `background-clip: text` needs `background-image: url()`, and that CSS property is NOT gated
 * by delayRender() — Remotion will happily render a frame before it loads, and knockout type
 * whose background hasn't arrived is INVISIBLE (color is transparent). Mounting a real <Img> for
 * every knockout source forces Remotion to wait, and the CSS then hits a warm cache.
 */
const Preload: React.FC = () => (
  <div style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", opacity: 0 }}>
    {Object.values(IMG).map((s) => (
      <Img key={s} src={s} style={{ width: 1, height: 1 }} />
    ))}
  </div>
);

export const VerC_Type: React.FC = () => (
  <AbsoluteFill style={{ backgroundColor: "#14100C" }}>
    <Audio src={sf("sanatan/audio/gotra45.mp3")} />
    <Preload />

    {/* ── P1 0-7 · THE SIX, STACKING ──────────────────────────────────────
        Each occasion arrives full-width; the stack scrolls up so the newest is always the
        biggest and the frame is always full. No cut, six states. */}
    <Plate from={0} to={7.0} lead={0}>
      <Field color="#1A1410" img={IMG.embers} wash={0.42} blend="screen" />
      <StackSix />
      <Label at={0.7} text="हर मौके पर" size={22} color="rgba(246,235,215,0.55)"
        style={{ position: "absolute", left: 74, top: 62, fontFamily: lab }} />
    </Plate>

    {/* ── P2 7-12.2 · THE QUESTION, KNOCKED OUT OF A CROWD ────────────────── */}
    <Plate from={7.0} to={12.2}>
      <Field color="#0E1218" img={IMG.crowd} wash={0.5} blend="luminosity" />
      <AbsoluteFill style={{ alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <Word at={7.22} text="हर बार, सबसे पहले" size={40} color="rgba(240,236,228,0.72)" font={lab} weight={600} />
          <div style={{ height: 12 }} />
          <Knockout at={10.69} text="आपका गोत्र" img={IMG.crowd} size={150} scale={1.6} />
          <div style={{ height: 2 }} />
          <Word at={11.49} text="क्या है" size={150} color="#E8B14A" font={hero} weight={400} />
        </div>
      </AbsoluteFill>
    </Plate>

    {/* ── P3 12.2-15.9 · THE CALL — the one cold frame ────────────────────── */}
    <Plate from={12.2} to={15.9}>
      <Field color="#101C24" img={IMG.stone} wash={0.3} blend="overlay" />
      <AbsoluteFill style={{ alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <Word at={12.43} text="और हम में से ज़्यादातर" size={46} color="rgba(214,232,244,0.7)" font={lab} weight={600} />
          <div style={{ height: 14 }} />
          <Word at={14.79} text="घर फ़ोन लगाते हैं" size={116} color="#DCEAF6" font={hero} weight={400} from="up" />
        </div>
      </AbsoluteFill>
    </Plate>

    {/* ── P4 15.9-19 · THE RESET ─────────────────────────────────────────── */}
    <Plate from={15.9} to={19.0}>
      <Field color="#8E2B1F" img={IMG.paper} wash={0.46} blend="multiply" />
      <AbsoluteFill style={{ alignItems: "flex-start", justifyContent: "center", padding: "0 84px" }}>
        <div>
          <Word at={15.95} text="सीधे काम की बात" size={104} color="#F6EEDD" font={hero} weight={400} />
          <div style={{ height: 16 }} />
          <Word at={17.67} text="गोत्र होता क्या है" size={56} color="rgba(246,238,221,0.82)" font={sec} weight={900} />
        </div>
      </AbsoluteFill>
    </Plate>

    {/* ── P5 19-22.7 · THREE WORDS, RECEDING ─────────────────────────────── */}
    <Plate from={19.0} to={22.7}>
      <Field color="#141A1A" img={IMG.stone} wash={0.36} blend="overlay" />
      <Tunnel3 />
    </Plate>

    {/* ── P6 22.7-29.9 · FOUR CELLS ──────────────────────────────────────── */}
    <Plate from={22.7} to={29.9}>
      <Field color="#12100E" img={IMG.paper} wash={0.28} blend="overlay" />
      <FourCells />
    </Plate>

    {/* ── P7 29.9-37.1 · THE ROOT, THEN THE SPLIT ────────────────────────── */}
    <Plate from={29.9} to={37.1}>
      <SplitSeven />
    </Plate>

    {/* ── P8 37.1-45.6 · ĀRṢA, AND THE CLIMB ─────────────────────────────── */}
    <Plate from={37.1} to={45.6} tail={0}>
      <ClimbEight />
    </Plate>

    <Grain opacity={0.2} freq={0.86} />
  </AbsoluteFill>
);

const StackSix: React.FC = () => {
  // the stack rises as each new item lands — always full, never a list sitting still
  const lift = useKeys(
    OCC.map(([t], i) => [t, i * 96] as [number, number]),
    Easing.out(Easing.cubic),
  );
  return (
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center" }}>
      <div style={{ transform: `translateY(${(160 - lift).toFixed(1)}px)`, textAlign: "center" }}>
        {OCC.map(([t, w], i) => (
          <div key={w} style={{ marginBottom: 6 }}>
            <Word
              at={t}
              text={w}
              size={i === OCC.length - 1 ? 96 : 92}
              color={i === OCC.length - 1 ? "#F2A03C" : `rgba(246,238,224,${(0.34 + i * 0.11).toFixed(2)})`}
              font={hero}
              weight={400}
              from="up"
            />
          </div>
        ))}
      </div>
    </AbsoluteFill>
  );
};

const Tunnel3: React.FC = () => {
  const push = useKeys([[19.0, 0], [22.7, 320]], Easing.inOut(Easing.cubic));
  const items: [number, string, number][] = [
    [19.2, "गोत्र", 0],
    [21.25, "प्रवर", 420],
    [22.02, "सपिंड", 840],
  ];
  return (
    <AbsoluteFill style={{ perspective: "1100px", alignItems: "center", justifyContent: "center" }}>
      <div style={{ transformStyle: "preserve-3d", transform: `translateZ(${push.toFixed(1)}px)` }}>
        {items.map(([t, w, z]) => (
          <TunnelWord key={w} at={t} text={w} z={z} />
        ))}
      </div>
    </AbsoluteFill>
  );
};

const TunnelWord: React.FC<{ at: number; text: string; z: number }> = ({ at, text, z }) => {
  const s = useSpringAt(at, { damping: 15, stiffness: 165 });
  return (
    <div
      style={{
        position: "absolute",
        left: "50%",
        top: "50%",
        transform: `translate(-50%,-50%) translateZ(${-z}px)`,
        transformStyle: "preserve-3d",
        opacity: s,
      }}
    >
      <span
        style={{
          ...devaBlock,
          fontFamily: hero,
          fontSize: 128,
          color: z === 0 ? "#F6EEDD" : `rgba(246,238,221,${(0.9 - z / 1600).toFixed(2)})`,
          whiteSpace: "pre",
        }}
      >
        {text}
      </span>
    </div>
  );
};

const FourCells: React.FC = () => {
  const cells: [number, string, string][] = [
    [22.75, "ये क्या हैं", IMG.paper],
    [23.66, "इनका महत्व", IMG.stone],
    [25.44, "शादी में क्या मिलता है", IMG.crowd],
    [27.72, "विज्ञान कितना है", IMG.embers],
  ];
  return (
    <AbsoluteFill style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr" }}>
      {cells.map(([t, w, img], i) => (
        <Cell key={w} at={t} text={w} img={img} accent={i === 3} />
      ))}
    </AbsoluteFill>
  );
};

const Cell: React.FC<{ at: number; text: string; img: string; accent: boolean }> = ({ at, text, img, accent }) => {
  const s = useKeys([[at, 0], [at + 0.3, 1]], Easing.out(Easing.cubic));
  return (
    <div style={{ position: "relative", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", padding: 26 }}>
      <Img
        src={img}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          opacity: 0.3 * s,
          filter: "saturate(0.3) contrast(1.2)",
          clipPath: `inset(0 ${(100 - s * 100).toFixed(1)}% 0 0)`,
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          border: `1px solid rgba(246,238,221,${(0.14 * s).toFixed(3)})`,
        }}
      />
      <span
        style={{
          ...devaBlock,
          position: "relative",
          fontFamily: sec,
          fontWeight: 900,
          fontSize: 44,
          textAlign: "center",
          color: accent ? "#F2A03C" : "rgba(246,238,221,0.92)",
          clipPath: `inset(-20% ${(100 - s * 100).toFixed(1)}% -18% -3%)`,
        }}
      >
        {text}
      </span>
    </div>
  );
};

const SplitSeven: React.FC = () => {
  const open = useKeys([[35.32, 0], [36.1, 1]], Easing.inOut(Easing.cubic));
  const rootOut = useKeys([[34.6, 1], [34.95, 0]], Easing.linear);
  return (
    <>
      <Field color="#0E1410" img={IMG.roots} wash={0.55} blend="screen" />
      <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", opacity: rootOut }}>
        <div style={{ textAlign: "center" }}>
          <Word at={29.94} text="इस पूरे विषय की" size={44} color="rgba(226,236,222,0.7)" font={lab} weight={600} />
          <div style={{ height: 6 }} />
          <Knockout at={32.36} text="जड़" img={IMG.roots} size={230} scale={1.9} />
        </div>
      </AbsoluteFill>
      {/* one word · two systems — the frame itself splits */}
      <AbsoluteFill style={{ opacity: open }}>
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(12,18,14,0.55)" }}>
          <Word at={35.58} text="पहली" size={92} color="#F6EEDD" font={hero} weight={400} />
        </div>
        <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(12,18,14,0.72)" }}>
          <Word at={35.84} text="दूसरी" size={92} color="rgba(246,238,221,0.45)" font={hero} weight={400} />
        </div>
        <div style={{ position: "absolute", left: "50%", top: "8%", bottom: "8%", width: 2, background: "#F2A03C", transform: `translateX(-50%) scaleY(${open.toFixed(3)})` }} />
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 62, textAlign: "center" }}>
          <Word at={37.30} text="दोनों असली हैं" size={38} color="rgba(246,238,221,0.85)" font={lab} weight={600} />
        </div>
      </AbsoluteFill>
    </>
  );
};

const ClimbEight: React.FC = () => {
  const step = useStep();
  const rise = useKeys([[43.4, 0], [45.6, 1]], Easing.out(Easing.cubic));
  return (
    <>
      <Field color="#1B1206" img={IMG.copper} wash={0.5} blend="screen" />
      <div style={{ position: "absolute", left: 78, top: 62 }}>
        <Knockout at={39.79} text="आर्ष गोत्र" img={IMG.copper} size={104} scale={1.7} />
      </div>
      <div style={{ position: "absolute", left: 82, top: 190 }}>
        <Word at={42.03} text="ऋषि से जुड़ा हुआ" size={44} color="rgba(240,224,196,0.85)" font={lab} weight={600} />
      </div>
      {/* the climb — three sizes of the same word receding, ending mid-move */}
      <div style={{ position: "absolute", right: 90, bottom: 92, textAlign: "right" }}>
        <div style={{ opacity: 1 }}>
          <Word at={43.81} text="पिता" size={96} color="#F6EEDD" font={hero} weight={400} />
        </div>
        <div style={{ transform: `translateY(${(-16 * rise).toFixed(1)}px)`, opacity: rise }}>
          <Word at={45.33} text="उनके पिता" size={72} color="#E8B14A" font={hero} weight={400} />
        </div>
        <div style={{ opacity: step >= 0 ? Math.max(0, rise - 0.7) * 2 : 0 }}>
          <span style={{ ...devaBlock, fontFamily: hero, fontSize: 52, color: "rgba(246,238,221,0.35)" }}>फिर उनके…</span>
        </div>
      </div>
    </>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
